import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { useGetGameAffiliatesQuery } from "@api";
import { MainContext } from "@context";

import { normalizeAffiliateKey } from "@layouts/betto/providers/providerAssets";
import {
	buildAffiliateProviderRoute,
	persistAffiliateProviderFilter,
} from "@pages/casino/utils/bettoProviderFilter";

import { getProviders } from "./utils";

const selectCurrency = (state) => state.balance.currency || undefined;
const isBettoTenant = () => window.TENANT === "betto";

const useController = ({ history, activeTab }) => {
	const {
		state: { isMobile, isTablet, isDesktop, isWide, isMinor },
	} = useContext(MainContext);

	const currency = useSelector(selectCurrency);

	const isLive = useMemo(() => {
		let currentTab = activeTab?.toLowerCase();

		const tabQueryParam = new URLSearchParams(window.location.search).get("tab");

		if (tabQueryParam) {
			currentTab = tabQueryParam;
		}

		switch (currentTab) {
			case "casino":
				return false;
			case "livecasino":
				return true;
			default:
				return null;
		}
	}, [activeTab]);

	const { data: affiliates } = useGetGameAffiliatesQuery({
		size: 1000,
		isLive: isLive,
		currency: currency,
	});

	const getSlidesPerGroup = () => {
		if (isMinor) {
			return 1;
		} else if (isMobile) {
			return 2;
		} else if (isTablet) {
			return 3;
		} else if (isDesktop) {
			return 4;
		} else if (isWide) {
			return 5;
		} else {
			return 6;
		}
	};

	const filteredAffiliates = (affiliates || [])?.filter((aff) => aff.affiliate !== "egt");

	const allProviders = getProviders();

	const filteredProviders = isBettoTenant()
		? (filteredAffiliates || [])
				.filter((aff) => aff.affiliate !== "egt")
				.map((aff) => aff.affiliate)
		: window.MARS_PROVIDERS
			? allProviders.filter((providerName) =>
					filteredAffiliates?.some(
						(affiliate) =>
							normalizeAffiliateKey(affiliate.affiliate) ===
							normalizeAffiliateKey(providerName)
					)
				)
			: allProviders.filter((providerName) =>
					filteredAffiliates?.some(
						(affiliate) =>
							normalizeAffiliateKey(affiliate.affiliate) ===
							normalizeAffiliateKey(providerName)
					)
				) || ["pragmaticplay"];

	const getTabSlug = () => {
		let currentTab = activeTab?.toLowerCase();

		const tabQueryParam = new URLSearchParams(window.location.search).get("page");

		if (tabQueryParam) {
			currentTab = tabQueryParam;
		}

		return currentTab === "livecasino" ? "livecasino" : "casino";
	};

	const handleSelectProvider = (providerName) => {
		const tabSlug = getTabSlug();
		const selectedProvider = filteredAffiliates?.find(
			(item) => normalizeAffiliateKey(item.affiliate) === normalizeAffiliateKey(providerName)
		);

		const affiliateKey = selectedProvider?.affiliate ?? providerName;
		const gameCount = selectedProvider?.count ?? 0;
		const provider = {
			key: affiliateKey,
			value: `${affiliateKey} (${gameCount})`,
			count: gameCount,
		};

		persistAffiliateProviderFilter(provider);
		history.push(buildAffiliateProviderRoute(provider, tabSlug));
	};

	return {
		state: {
			providers: filteredProviders,
			slidesPerGroup: getSlidesPerGroup(),
		},
		handlers: {
			handleSelectProvider,
		},
	};
};

export default useController;
